import { Box, Button, Grid, Heading, useMediaQuery } from '@chakra-ui/react';
import { Image } from '@components/AssetWrapper';
import { AppLink, Card, LANDING_ROUTES, TabsContainer, TabsItem } from '@cyberkongz/ui';
import { LandingKollectionType } from '@features/kollections';
import { KongVXTheme, useSharedUI } from '@features/sharedUI/SharedUIProvider';
import { ReactNode, useEffect, useState } from 'react';

import { Label } from './Shared';

type TabType = 'info' | 'traits' | 'effects';

const TABS: { label: string; id: TabType }[] = [
  {
    label: 'Info',
    id: 'info',
  },
  {
    label: 'Traits',
    id: 'traits',
  },
  {
    label: 'Effects',
    id: 'effects',
  },
];

const EFFECTS_GENESIS = ['tunnel.jpg', 'synth.jpg', 'gen_hall.jpg'];

const EFFECTS_BABY = ['tunnel.jpg', 'synth.jpg', 'myoo_wsk.jpg'];

const EFFECTS_VX = ['grid.jpg', 'synth.jpg', 'portal.jpg', 'shrine.jpg'];

const EFFECTS_BY_KONG_TYPE: { [key in LandingKollectionType]: string[] } = {
  genesis: EFFECTS_GENESIS,
  baby: EFFECTS_BABY,
  vx: EFFECTS_VX,
};

function Effects({
  type,
  rawCssColors,
}: {
  type: LandingKollectionType;
  rawCssColors?: KongVXTheme;
}) {
  const { setKongVXTheme, kongVXTheme } = useSharedUI();

  return (
    <Box>
      <Grid css={{ gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        {EFFECTS_BY_KONG_TYPE[type].map((effect) => (
          <Image
            key={effect}
            onClick={() => setKongVXTheme({ bgEffect: effect })}
            src={`/static/kong_page/effects/${effect}`}
            cursor="pointer"
            alt="Kong effect background thumbnail"
          />
        ))}
      </Grid>
      {kongVXTheme?.bgEffect ? (
        <Button
          mt={4}
          w="100%"
          variant="display"
          onClick={() => setKongVXTheme({ ...rawCssColors, bgEffect: undefined })}
        >
          Reset
        </Button>
      ) : null}
    </Box>
  );
}

function Traits({
  type,
  attributes,
}: {
  type: LandingKollectionType;
  attributes: PrismaJson.KongAttributesType | null;
}) {
  const { kongVXTheme } = useSharedUI();

  return (
    <Box>
      {Array.isArray(attributes) ? (
        <Grid css={{ gap: 4 }}>
          {attributes.map(({ trait_type, value }) => (
            <AppLink
              key={`${trait_type}-${value}`}
              href={{
                pathname: LANDING_ROUTES.KOLLECTIONS,
                query: {
                  kollection: type,
                  stringTraits: JSON.stringify([{ traitType: trait_type, values: [value] }]),
                },
              }}
              css={{ '&:hover': { textDecorationColor: kongVXTheme?.text } }}
            >
              <Card
                variant="landingTransparent"
                key={`${trait_type}-${value}`}
                css={{
                  p: 0,
                  py: 3,
                  px: 4,
                  bg: 'rgba(255, 255, 255, 0.15)',
                }}
              >
                <Label color={kongVXTheme?.label}>{trait_type}</Label>
                <Box color={kongVXTheme?.text || 'text'}>{value}</Box>
              </Card>
            </AppLink>
          ))}
        </Grid>
      ) : (
        <Box>This Kong has no traits</Box>
      )}
    </Box>
  );
}

export function KongTabs({
  type,
  infoComponent,
  attributes,
  rawCssColors,
}: {
  type: LandingKollectionType;
  infoComponent: ReactNode;
  attributes: PrismaJson.KongAttributesType | null;
  rawCssColors?: KongVXTheme;
}) {
  const { kongVXTheme } = useSharedUI();
  const [isMobile] = useMediaQuery(['(max-width: 1023px)'], {
    ssr: true,
    fallback: [false],
  });
  const [activeTab, setActiveTab] = useState<TabType>('traits');

  useEffect(() => {
    setActiveTab(isMobile ? 'info' : 'traits');
  }, [isMobile]);

  return (
    <Box css={{ position: 'relative', zIndex: 2 }}>
      <TabsContainer css={{ mb: 5 }}>
        {TABS.map(({ id, label }) => {
          return (type === 'vx' && id !== 'effects') || type !== 'vx' ? (
            <TabsItem
              {...{
                activeTab,
                setActiveTab,
                tab: id,
                key: id,
                css: id === 'info' ? { display: ['block', null, 'none'] } : undefined,
                color: kongVXTheme?.textTab,
              }}
            >
              <Heading size="2xs" color="currentColor">
                {label}
              </Heading>
            </TabsItem>
          ) : null;
        })}
      </TabsContainer>
      {activeTab === 'effects' ? <Effects {...{ type, rawCssColors }} /> : null}
      {activeTab === 'traits' ? <Traits {...{ attributes, type }} /> : null}
      {activeTab === 'info' ? infoComponent : null}
    </Box>
  );
}
